import { Button } from '@/components/ui/Button'
import type { AutoApplySuggestion } from '@/types/api'
import { formatNumber } from './filters'

/**
 * Few or no jobs: ways to widen the search, each with how many more it finds.
 * Above the list it is a row of chips; with nothing found it is the empty
 * state, one row per suggestion with its reason.
 */
export function SuggestionsCard({
  ready,
  suggestions,
  onPick,
  onClearAll,
  hasFilters,
}: {
  ready: number
  suggestions: AutoApplySuggestion[]
  onPick: (suggestion: AutoApplySuggestion) => void
  onClearAll: () => void
  hasFilters: boolean
}) {
  if (ready === 0) {
    return (
      <section className="card overflow-hidden" aria-label="No jobs match yet">
        <div className="flex flex-col items-center px-5 pb-4 pt-8 text-center sm:pt-10">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-surface-muted text-ink-muted">
            <WidenIcon className="h-5 w-5" />
          </span>
          <h2 className="mt-3 text-[16px] font-semibold text-ink">No jobs match these filters yet</h2>
          <p className="mt-1 max-w-md text-[13.5px] leading-relaxed text-ink-muted">
            {suggestions.length
              ? 'Widen your search a little to get more chances. Each option shows how many more jobs it finds.'
              : 'Try removing a filter or lowering the match score.'}
          </p>
        </div>
        {suggestions.length > 0 && (
          <ul className="mx-4 mb-4 divide-y divide-line overflow-hidden rounded-field border border-line sm:mx-6">
            {suggestions.map((suggestion) => (
              <li key={suggestion.id} className="flex items-center gap-3 px-3.5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-ink">{suggestion.label}</p>
                  {suggestion.detail && <p className="mt-0.5 text-[12.5px] leading-snug text-ink-muted">{suggestion.detail}</p>}
                </div>
                <Button size="sm" variant="secondary" className="shrink-0" onClick={() => onPick(suggestion)}>
                  <span className="num font-semibold text-brand">+{formatNumber(suggestion.adds)}</span>
                  <span className="hidden xs:inline">Show</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
        {hasFilters && (
          <div className="flex justify-center border-t border-line px-4 py-3">
            <Button size="sm" variant="ghost" onClick={onClearAll}>
              Clear all filters
            </Button>
          </div>
        )}
      </section>
    )
  }

  if (!suggestions.length) return null

  return (
    <section className="rounded-card border border-brand/30 bg-brand-soft/40 px-4 py-3.5 sm:px-5" aria-label="Widen your search">
      <div className="flex items-start gap-2.5">
        <WidenIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold text-ink">
            Only <span className="num">{formatNumber(ready)}</span> job{ready === 1 ? '' : 's'} match. Widen your search for more chances.
          </h2>
          {suggestions[0]?.detail && <p className="mt-0.5 text-[12.5px] leading-snug text-ink-muted">{suggestions[0].detail}</p>}
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                type="button"
                title={suggestion.detail || undefined}
                onClick={() => onPick(suggestion)}
                className="tap inline-flex h-8 items-center gap-1.5 rounded-pill border border-line bg-surface px-3 text-[13px] text-ink shadow-card transition-colors hover:border-line-strong"
              >
                {suggestion.label}
                <span className="num font-semibold text-brand">+{formatNumber(suggestion.adds)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function WidenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 7V3h4M17 7V3h-4M3 13v4h4M17 13v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="10" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}
